export const CurrencyCode = {
  INR: 'INR',
  USD: 'USD',
} as const;

export type CurrencyCode = typeof CurrencyCode[keyof typeof CurrencyCode];

export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/** Today as DD-MM-YYYY, the format the contract-file screens' `date` param uses. */
export const todayDateParam = (): string => {
    const today = new Date();
    return [
        String(today.getDate()).padStart(2, '0'),
        String(today.getMonth() + 1).padStart(2, '0'),
        today.getFullYear(),
    ].join('-');
};

export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const datePart = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart} ${timePart}`;
};

export const formatNumber = (num: number, decimals = 2): string => {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

/** Share quantities: up to 2 decimals, none when whole — "10", "10.5", "1,234.25". */
export const formatQuantity = (num: number): string =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num || 0);

const currencyFormatters = new Map<string, Intl.NumberFormat>();

const getCurrencyFormatter = (currencyCode: string, decimals: number): Intl.NumberFormat => {
  const code = currencyCode || CurrencyCode.INR; // guard against empty string from API
  const cacheKey = `${code}:${decimals}`;
  if (!currencyFormatters.has(cacheKey)) {
    const locale = code === CurrencyCode.INR ? 'en-IN' : 'en-US';
    let formatter: Intl.NumberFormat;
    try {
      formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    } catch {
      // Unrecognised currency code from the API — fall back to INR instead of
      // letting Intl's RangeError crash the page.
      formatter = getCurrencyFormatter(CurrencyCode.INR, decimals);
    }
    currencyFormatters.set(cacheKey, formatter);
  }
  return currencyFormatters.get(cacheKey)!;
};

export const formatCurrency = (value: number | string | null | undefined, currencyCode: string = CurrencyCode.INR, decimals = 2): string => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '—';
  }
  return getCurrencyFormatter(currencyCode, decimals).format(numValue);
};

/**
 * Formats a price for Domestic/Global display. Domestic rows are prefixed with
 * `domesticSymbol` (default "₹"; pass "" to omit the symbol, e.g. a CMP cell). Global
 * (non-Indian) rows append the ISO currency code in brackets, e.g. "1,234.50 (USD)" — or
 * a bare number when the code is unknown. Null/empty/NaN values render as "—".
 */
export const formatPrice = (
  value: number | string | null | undefined,
  currencyCode: string | null | undefined,
  isGlobal: boolean,
  domesticSymbol = '₹',
): string => {
  if (value === undefined || value === null || value === '') return '—';
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(numValue)) return '—';
  const formatted = formatNumber(numValue, 2);
  return isGlobal
    ? `${formatted}${currencyCode ? ` (${currencyCode})` : ''}`
    : `${domesticSymbol}${formatted}`;
};

/**
 * Total for an order summary. A null `currencyCode` means the page mixes domestic and
 * global orders, so the amount spans currencies and is shown without a symbol rather than
 * under a misleading one. Null, empty and unparseable values render as "—".
 */
export const formatSummaryAmount = (
  value: number | string | null | undefined,
  currencyCode: string | null | undefined,
): string => (currencyCode ? formatCurrency(value, currencyCode) : formatPrice(value, null, true));

export const formatSchemeLabel = (
  s: { code?: string; acronym?: string; display_name?: string; name?: string; id?: number },
  options?: { includeAcronym?: boolean }
): string => {
  const includeAcronym = options?.includeAcronym ?? true;
  const base = s.code ?? String(s.id ?? '');
  const namePart = s.display_name ? ` - ${s.display_name}` : s.name ? ` - ${s.name}` : '';
  return `${base}${namePart}`;
};

/**
 * Formats an amount in crores (INR) or millions/thousands (USD).
 *
 * `decimals` applies to the INR crore form only, and defaults to 0. Pass 2 where a
 * sub-crore figure would otherwise collapse to "₹0 Cr" — a block deal's realised P&L, for
 * instance, can be a fraction of a crore and still be the number the decision turns on.
 */
export const formatCurrencyInCrores = (
  value: number,
  baseCurrency?: string,
  bare?: boolean,
  decimals = 0,
): string => {
  const currency = baseCurrency || CurrencyCode.INR;
  if (bare) {
    if (currency === CurrencyCode.USD) {
      return Math.abs(value) >= 1_000_000
        ? formatNumber(value / 1_000_000, 2)
        : formatNumber(value / 1000, 2);
    }
    return formatNumber(value / 10_000_000, 2);
  }
  if (currency === CurrencyCode.USD) {
    if (Math.abs(value) >= 1_000_000) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value / 1_000_000) + 'M';
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value / 1000) + 'k';
  }
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value / 10_000_000) + ' Cr';
};

/**
 * Formats a share count in lakhs, the unit a dealing desk quotes block sizes in
 * (1 lakh = 100,000). Null/NaN render as "—" — a missing quantity is not zero.
 * Pass `bare` to omit the " L" suffix, e.g. inside a column already headed "Qty (L)".
 */
export const formatQuantityInLakhs = (
  value: number | string | null | undefined,
  decimals = 2,
  options?: { bare?: boolean },
): string => {
  if (value === undefined || value === null || value === '') return '—';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '—';
  const inLakhs = formatNumber(numValue / 100_000, decimals);
  return options?.bare ? inLakhs : `${inLakhs} L`;
};

export const formatRelativeTime = (iso: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  if (diff < 60000) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const formatPercentage = (
  value: number | string | null | undefined,
  decimals = 2,
  options?: { bare?: boolean; fromFraction?: boolean }
): string => {
  if (value === undefined || value === null || value === '') return '—';
  let numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '—';
  if (options?.fromFraction) numValue = numValue * 100;
  if (options?.bare) return formatNumber(numValue, decimals);
  return `${parseFloat(numValue.toFixed(decimals))}%`;
};
/**
 * A client's display name: `full_name`, else "first [middle ]last".
 * Use this rather than rebuilding the name in each list.
 */
export const formatClientName = (c: {
  full_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
}): string =>
  c.full_name
  || `${c.first_name || ''} ${c.middle_name ? c.middle_name + ' ' : ''}${c.last_name || ''}`.trim();
